import cloudSyncService from './cloudSyncService';
import { sanitizeDataForStorage } from '../utils/storage';
import {
  COMPANY_KEY,
  CLIENTS_KEY,
  PRODUCTS_KEY,
  INVOICES_KEY,
  INVOICE_DETAILS,
} from '../constants/localKeys';
import secureLogger from '../utils/secureLogger';
import * as assetStorage from './assetStorage';

// Enhanced storage utility with automatic cloud sync.
class SyncedStorageService {
  constructor() {
    this.syncDebounceTimeout = null;
    this.syncQueue = new Set();
    this.isInitialized = false;
  }

  initialize() {
    this.isInitialized = true;
  }

  // setItem: persist locally then ping cloudSync. Writes that happen during a
  // cloud apply (cloudApplyDepth > 0) skip the trigger because they're
  // replaying remote data — triggering again would echo cloud rows back.
  async setItem(key, value, skipSync = false) {
    try {
      const sanitized = sanitizeDataForStorage(value);
      const isApplyingCloud = cloudSyncService && cloudSyncService.cloudApplyDepth > 0;

      // assetStorage compacts image-carrying values before hitting IDB and
      // updates the shared _assets map; non-image keys passthrough to
      // localforage untouched.
      const result = await assetStorage.setItem(key, sanitized);

      if (!skipSync && !isApplyingCloud && cloudSyncService) {
        cloudSyncService.triggerSync(key, 'update');
      }

      return result;
    } catch (error) {
      secureLogger.error(`Failed to store ${key}:`, error);
      throw error;
    }
  }

  async getItem(key) {
    try {
      return await assetStorage.getItem(key);
    } catch (error) {
      secureLogger.error(`Failed to retrieve ${key}:`, error);
      throw error;
    }
  }

  // removeItem: only used by Sidebar's "Clear data" today.
  async removeItem(key, skipSync = false) {
    try {
      const result = await assetStorage.removeItem(key);

      const isApplyingCloud = cloudSyncService && cloudSyncService.cloudApplyDepth > 0;
      if (!skipSync && !isApplyingCloud && cloudSyncService) {
        cloudSyncService.triggerSync(key, 'delete');
      }

      return result;
    } catch (error) {
      secureLogger.error(`Failed to remove ${key}:`, error);
      throw error;
    }
  }

  queueSync(changedKey) {
    if (!this.isInitialized) return;
    this.syncQueue.add(changedKey);
    if (this.syncDebounceTimeout) clearTimeout(this.syncDebounceTimeout);
    this.syncDebounceTimeout = setTimeout(() => this.performDebouncedSync(), 2000);
  }

  async performDebouncedSync() {
    if (this.syncQueue.size === 0) return;
    this.syncQueue.clear();
    try {
      const syncStatus = cloudSyncService.getSyncStatus();
      if (syncStatus.hasToken && syncStatus.isOnline) {
        await cloudSyncService.performSync('auto_data_change');
      }
    } catch (error) {
      secureLogger.warn('Auto-sync failed:', error);
    }
  }

  clearSync() {
    if (this.syncDebounceTimeout) {
      clearTimeout(this.syncDebounceTimeout);
      this.syncDebounceTimeout = null;
    }
    this.syncQueue.clear();
  }

  getSyncQueueStatus() {
    return {
      queuedKeys: Array.from(this.syncQueue),
      hasPendingSync: this.syncDebounceTimeout !== null,
    };
  }

  async performAutoSync() {
    try {
      if (cloudSyncService && cloudSyncService.isEnabled()) {
        await cloudSyncService.sync();
      }
    } catch (error) {
      secureLogger.warn('Auto-sync failed:', error);
    }
  }
}

export const syncedStorage = new SyncedStorageService();

export const saveDataWithSync = async (key, data) => syncedStorage.setItem(key, data);
export const getDataWithSync = async (key) => syncedStorage.getItem(key);
export const removeDataWithSync = async (key) => syncedStorage.removeItem(key);

export const saveClientData = async (clients) => saveDataWithSync(CLIENTS_KEY, clients);
export const saveProductData = async (products) => saveDataWithSync(PRODUCTS_KEY, products);
export const saveInvoiceData = async (invoices) => saveDataWithSync(INVOICES_KEY, invoices);
export const saveInvoiceDetailData = async (details) => saveDataWithSync(INVOICE_DETAILS, details);
export const saveCompanyData = async (company) => saveDataWithSync(COMPANY_KEY, company);

syncedStorage.initialize();

export default syncedStorage;
